import React, { useEffect, useMemo, useRef, useState } from "react";
import { listYtTracks } from "../lib/api";

/**
 * DirectorYtPlayer
 *
 * Plays a per-scene playlist of YouTube tracks (added via Admin) in the
 * background of the Director view. The player itself is positioned
 * off-screen so viewers only hear the audio — but browsers require a
 * mounted <iframe> for the YouTube IFrame Player API to load audio.
 *
 * Behaviour:
 *  - When the scene changes, playback resets to track 0 of the new scene.
 *  - When a new track is ADDED to the current scene mid-playback, the
 *    playing track is NOT interrupted. The new track will play right
 *    after the currently-playing one finishes.
 *  - When the last track ends, playback loops back to track 0.
 */
export default function DirectorYtPlayer({ scene }) {
  const [tracks, setTracks] = useState([]);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const readyRef = useRef(false);
  const [apiReady, setApiReady] = useState(!!window.YT?.Player);

  // Load the tracks list once and refresh every 30s (so newly-added songs
  // appear without a full reload).
  useEffect(() => {
    let alive = true;
    const load = () =>
      listYtTracks()
        .then((rows) => { if (alive) setTracks(rows || []); })
        .catch(() => {});
    load();
    const tm = setInterval(load, 30000);
    return () => { alive = false; clearInterval(tm); };
  }, []);

  // Inject the YouTube IFrame API script once.
  useEffect(() => {
    if (window.YT?.Player) { setApiReady(true); return; }
    if (document.getElementById("yt-iframe-api")) {
      const tm = setInterval(() => {
        if (window.YT?.Player) { setApiReady(true); clearInterval(tm); }
      }, 300);
      return () => clearInterval(tm);
    }
    const tag = document.createElement("script");
    tag.id = "yt-iframe-api";
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => setApiReady(true);
  }, []);

  // Tracks for the current scene
  const sceneTracks = useMemo(
    () => (tracks || []).filter((t) => t.scene === scene && t.video_id),
    [tracks, scene]
  );
  const videoIds = useMemo(() => sceneTracks.map((t) => t.video_id), [sceneTracks]);

  // Always-fresh refs so the ENDED handler can see the latest playlist
  // without being re-bound (avoids stale-closure loop-on-1st-video bug).
  const videoIdsRef = useRef(videoIds);
  const currentIndexRef = useRef(0);
  const sceneRef = useRef(scene);
  useEffect(() => { videoIdsRef.current = videoIds; }, [videoIds]);

  // Build / update player. Only truly restarts on scene change; new tracks
  // added within the same scene queue up naturally after the current song.
  useEffect(() => {
    if (!apiReady || !containerRef.current) return;

    const sceneChanged = sceneRef.current !== scene;
    sceneRef.current = scene;

    if (!videoIds.length) {
      try { playerRef.current?.stopVideo?.(); } catch { /* ignore */ }
      return;
    }

    if (!playerRef.current) {
      // First-time construction — start at track 0.
      currentIndexRef.current = 0;
      playerRef.current = new window.YT.Player(containerRef.current, {
        height: "100",
        width: "160",
        videoId: videoIds[0],
        playerVars: {
          autoplay: 1,
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            readyRef.current = true;
            try {
              e.target.setVolume(60);
              e.target.playVideo();
            } catch { /* */ }
          },
          onStateChange: (e) => {
            // 0 = ended → advance to next track using the LATEST list.
            if (e.data === window.YT?.PlayerState?.ENDED) {
              const ids = videoIdsRef.current;
              if (!ids.length) return;
              const next = (currentIndexRef.current + 1) % ids.length;
              currentIndexRef.current = next;
              try { e.target.loadVideoById(ids[next]); } catch { /* */ }
            }
          },
        },
      });
      return;
    }

    // Player already exists.
    if (sceneChanged && readyRef.current) {
      // Scene actually changed → reset to first track of the new scene.
      currentIndexRef.current = 0;
      try {
        playerRef.current.loadVideoById(videoIds[0]);
        playerRef.current.playVideo();
      } catch { /* */ }
    }
    // Else: same scene, track list mutated (add / delete).
    // Do NOTHING here — videoIdsRef is updated, and the ENDED handler will
    // pick up the newly-added track after the current song naturally ends.
    // If the currently-playing index is now stale (e.g. current track was
    // deleted), the modulo in ENDED will wrap safely.
  }, [apiReady, scene, videoIds.length]);

  // On unmount, tear down the player cleanly.
  useEffect(() => {
    return () => {
      try { playerRef.current?.destroy?.(); } catch { /* */ }
      playerRef.current = null;
    };
  }, []);

  if (!videoIds.length) return null;

  // Off-screen positioning — audio still plays.
  return (
    <div
      aria-hidden="true"
      data-testid="director-yt-player"
      style={{
        position: "fixed",
        width: 160,
        height: 100,
        left: -9999,
        top: -9999,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <div ref={containerRef} />
    </div>
  );
}
